<template>
  <v-container v-if="login">
    <v-row>
      <v-col>
        <h1>Department Hours Manager</h1>
        <h2>Offices and Departments</h2>
        <p class="description">
          Only authorized personnel are able to manage their departmental hours
          in the
          <a href="https://www.adelphi.edu/departments/" target="_blank"
            >institutional directory</a
          >. If you have any questions, please contact the Digital Team at
          <a href="mailto:digital@adelphi.edu">digital@adelphi.edu</a>.
        </p>
        <div></div>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-text-field
          v-model="search"
          model-value="John Doe"
          outlined
          :style="{ width: '320px' }"
          append-icon="mdi-magnify"
        >
          <template v-slot:label>
            <span class="custom-label">Search Departments</span>
          </template>
        </v-text-field>
        <v-data-table
          :headers="headers"
          :items="paginatedDepartments"
          :items-per-page="itemsPerPage"
          hide-default-footer
          hide-default-header
          class="elevation-1"
          :class="{ 'custom-header': true }"
        >
          <template v-slot:header="{ props: { headers } }">
            <thead>
              <tr>
                <th
                  v-for="h in headers"
                  :key="h.value"
                  class="custom-table-header"
                >
                  <span class="custom-table-header-text">{{ h.text }}</span>
                </th>
              </tr>
            </thead>
          </template>
          <template v-slot:body="{ items }">
            <tbody>
              <tr
                v-for="(item, index) in items"
                :key="item.code"
                :class="{ 'even-row': index % 2 === 1 }"
              >
                <td class="department-row">
                  <p>{{ item.title }} ({{ item.code }})</p>

                  <v-btn @click="editItem(item)" class="edit-btn">
                    <v-icon left>mdi-pencil</v-icon>
                    Edit</v-btn
                  >
                </td>
              </tr>
            </tbody>
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <v-pagination
          v-model="page"
          :length="pageCount"
          :total-visible="7"
          circle
        >
        </v-pagination>
      </v-col>
    </v-row>

    <v-row v-if="isAdmin == true">
      <v-col>
        <v-sheet elevation="3" class="my-custom-sheet" rounded>
          <div class="form-title">
            <p class="form-title-text">Hours Tool User Management</p>
            <v-icon v-if="showUsers === false" @click="showUsers = true">
              mdi-chevron-down
            </v-icon>
            <v-icon v-if="showUsers === true" @click="showUsers = false">
              mdi-chevron-up
            </v-icon>
          </div>
          <div :style="{ padding: '0px 30px' }" v-if="showUsers === true">
            <div
              v-for="(user, index) in departmentScheduleUsers"
              :key="user.id"
            >
              <v-divider></v-divider>
              <div
                :style="{ paddingTop: '10px', paddingBottom: '10px' }"
                class="department-row"
              >
                <p>{{ index + 1 }}. {{ user.email }}</p>
                <div
                  :style="{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '15px',
                  }"
                >
                  <v-btn
                    @click="handleShowUserDepartmentCodes(user.id)"
                    class="edit-btn"
                  >
                    <v-icon left>mdi-pencil</v-icon>
                    {{
                      showUserDepartmentCodes[user.id] ? "Hide" : "Show"
                    }}</v-btn
                  >
                  <v-btn @click="deleteUser(user.id)" class="delete-btn">
                    <v-icon left color="white"
                      >mdi-delete-forever-outline</v-icon
                    >
                    Delete</v-btn
                  >
                </div>
              </div>
              <div v-if="showUserDepartmentCodes[user.id] === true">
                <div
                  v-for="departmentCode in user.department_codes"
                  :key="departmentCode.id"
                  :style="{
                    padding: '10px 20px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '20px',
                  }"
                >
                  <p
                    :style="{
                      margin: 0,
                    }"
                  >
                    {{
                      getDepartmentTitleByCode(departmentCode.department_code)
                    }}
                  </p>
                  <v-icon
                    @click="
                      handleDeleteDepartmentCode(user.id, departmentCode.id)
                    "
                    >mdi-close-circle</v-icon
                  >
                </div>

                <div
                  :style="{
                    margin: '10px 20px',
                    display: 'flex',
                    alignItems: 'flex-end',
                    gap: '20px',
                  }"
                >
                  <v-text-field
                    v-model="newDepartmentCode[user.id]"
                    :style="{
                      height: '30px',
                      maxWidth: '300px',
                      padding: '0',
                      margin: '0',
                    }"
                  >
                    <template v-slot:label>
                      <span :style="{ color: '#858585', fontWeight: '300' }"
                        >New Department Code</span
                      >
                    </template>
                  </v-text-field>
                  <v-btn
                    @click="handleAddNewDepartmentCode(user.id)"
                    class="edit-btn"
                  >
                    <v-icon left>mdi-plus-circle-outline</v-icon>
                    Add
                  </v-btn>
                </div>
              </div>
            </div>
            <div
              :style="{
                padding: '16px 0px',
                display: 'flex',
                alignItems: 'center',
                gap: '15px',
              }"
            >
              <v-text-field
                v-model="newUserEmail"
                class="user-container"
                outlined
                :rules="[
                  (v) => !!v || 'User email is required',
                  (v) =>
                    !departmentScheduleUsers.some((user) => user.email === v) ||
                    'Email already exists!',
                  (v) =>
                    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ||
                    'Must be a valid email address',
                ]"
                ref="newUser"
              >
                <template v-slot:label>
                  <span class="custom-label">New User Email</span>
                </template>
              </v-text-field>
              <v-btn
                @click="handleAddNewUser()"
                class="edit-btn"
                :style="{ marginBottom: '30px' }"
              >
                <v-icon left>mdi-plus-circle-outline</v-icon>
                Add
              </v-btn>
            </div>
          </div>
        </v-sheet>
      </v-col>
    </v-row>
    <Dialog
      :dialog.sync="deleteUserDialog"
      @confirm="handleDeleteUser"
      title="Delete User?"
      message="You will loose all the information entered for this user."
      btn2Text="DELETE USER"
      btn1Text="KEEP IT"
    />
    <Dialog
      :dialog.sync="sessionExpireDialog"
      @confirm="handleSessionExpire"
      title="Session Expired"
      message="The current session is expired. Please try it again!"
      btn2Text="BACK"
    />
  </v-container>
</template>

<script>
import axios from "axios";
import ClientOAuth2 from "client-oauth2";
import Dialog from "./hourManager/Dialog.vue";
export default {
  data() {
    return {
      login: false,
      apiUrl: process.env.VUE_APP_API_BASE_URL,
      search: "",
      page: 1,
      itemsPerPage: 10,
      // dialog: false,
      selectedDepartment: {},
      headers: [
        { text: "Department", value: "title", sortable: false },
        // { text: "", value: "action", sortable: false },
      ],
      departments: [],
      departmentScheduleUsers: [],
      showUsers: false,
      showUserDepartmentCodes: {},
      newUserEmail: "",
      isAdmin: false,
      deleteUserDialog: false,
      selectedUserId: null,
      newDepartmentCode: {},
      sessionExpireDialog: false,
    };
  },
  components: {
    Dialog,
  },

  created() {
    // const oauthUri = localStorage.getItem('oauthUri');
    // if (oauthUri) {
    //   localStorage.removeItem('oauthUri');
    //   this.handleOAuthCallback(oauthUri); // Process the OAuth callback
    // }else {
    //   this.handleOAuth();
    // }
    this.handleOAuth();
    this.fetchDepartmentData();
  },
  update() {},
  // mounted() {
  //   window.addEventListener("beforeunload", this.clearAccessToken);
  // },
  // beforeDestroy() {
  //   window.removeEventListener("beforeunload", this.clearAccessToken);
  // },
  computed: {
    filteredDepartments() {
      if (this.search) {
        return this.departments.filter((department) =>
          department.title.toLowerCase().includes(this.search.toLowerCase())
        );
      }
      return this.departments;
    },

    paginatedDepartments() {
      return this.filteredDepartments.slice(
        (this.page - 1) * this.itemsPerPage,
        this.page * this.itemsPerPage
      );
    },
    pageCount() {
      return Math.ceil(this.filteredDepartments.length / this.itemsPerPage);
    },
  },
  methods: {
    async fetchDepartmentData() {
      try {
        this.isAdmin = sessionStorage.getItem("isAdmin") === "true";
        const response = await axios.get(
          `${this.apiUrl}/v1/departments/?no_pagination=true`
        );
        const filteredData = response.data.filter((d) => d.active === true);
        if (this.isAdmin) {
          this.departments = filteredData;
        } else {
          const userDepartments = JSON.parse(
            sessionStorage.getItem("userDepartments")
          );
          this.departments = filteredData.filter((data) =>
            userDepartments.some(
              (department) => department.department_code === data.code
            )
          );
        }

        const response2 = await axios.get(
          `${this.apiUrl}/v1/department_schedule_user/?no_pagination=true`
        );

        this.departmentScheduleUsers = response2.data;
      } catch (error) {
        window.console.error("Error fetching data:", error);
      }
    },
    editItem(item) {
      // this.selectedDepartment = { ...item };
      // this.dialog = true;
      this.$router.push({
        path: `/office_hour_manager/${item.id}`,
        query: { department: encodeURIComponent(JSON.stringify(item)) },
      });
    },
    updateDepartment(updatedDepartment) {
      const index = this.departments.findIndex(
        (department) => department.title === updatedDepartment.title
      );
      if (index !== -1) {
        this.departments.splice(index, 1, updatedDepartment);
      }
    },
    updatePage(page) {
      this.page = page;
    },
    async handleOAuth() {
      const adelphiAuth = new ClientOAuth2({
        clientId: process.env.VUE_APP_OAUTH_CLIENT_ID,
        clientSecret: process.env.VUE_APP_OAUTH_CLIENT_SECRET,
        authorizationUri: process.env.VUE_APP_AUTHORIZATION_URL,
        redirectUri: process.env.VUE_APP_REDIRECT_URL,
        // redirectUri: window.location.origin + window.location.pathname,
        scopes: ["email", "groups", "openid"],
        state:
          parseInt(Math.random() * 1e12) +
          "_" +
          parseInt(Math.random() * 1e12) +
          "_" +
          parseInt(Math.random() * 1e12),
      });
      window.oauth2Callback = (uri) => {
        adelphiAuth.token.getToken(uri).then((user) => {
          this.login = true;
          const expiryTime = new Date().getTime() + user.data.expires_in * 1000;
          sessionStorage.setItem("accessToken", user.accessToken);
          sessionStorage.setItem("expiryTime", expiryTime);
        });
      };

      window.open(adelphiAuth.token.getUri(), "_blank");
      // window.location.href = adelphiAuth.token.getUri();
    },
    // handleOAuthCallback(uri) {
    //   const adelphiAuth = new ClientOAuth2({
    //     clientId: process.env.VUE_APP_OAUTH_CLIENT_ID,
    //     clientSecret: process.env.VUE_APP_OAUTH_CLIENT_SECRET,
    //     authorizationUri: process.env.VUE_APP_AUTHORIZATION_URL,
    //     redirectUri: process.env.VUE_APP_REDIRECT_URL,
    //     scopes: ["email", "groups", "openid"],
    //   });
    //   window.console.log("check handle oatuht called1111111",adelphiAuth)
    //   adelphiAuth.token.getToken(uri).then((user) => {
    //     window.console.log("check handle oatuht called222222222222",user)
    //     this.login = true;
    //     const expiryTime = new Date().getTime() + user.data.expires_in * 1000;
    //     sessionStorage.setItem("accessToken", user.accessToken);
    //     sessionStorage.setItem("expiryTime", expiryTime);
    //   });
    // },

    async deleteUser(userId) {
      this.selectedUserId = userId;
      this.deleteUserDialog = true;
    },
    handleShowUserDepartmentCodes(userId) {
      if (this.showUserDepartmentCodes[userId] !== undefined) {
        this.showUserDepartmentCodes[userId] =
          !this.showUserDepartmentCodes[userId];
      } else {
        this.$set(this.showUserDepartmentCodes, userId, true);
      }
    },
    async handleAddNewUser() {
      if (!this.$refs.newUser.validate()) {
        this.$nextTick(() => {
          const element = this.$refs.newUser;
          const domElement = element.$el || element;

          if (domElement.scrollIntoView) {
            domElement.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
          if (typeof domElement.focus === "function") {
            domElement.focus();
          }
        });
      } else {
        if (this.checkSessionExpire()) {
          return;
        }
        const accessToken = sessionStorage.getItem("accessToken");
        try {
          const newPost = {
            email: this.newUserEmail,
            department_codes: [],
          };

          const response = await axios.post(
            `${this.apiUrl}/v1/department_schedule_user/`,
            newPost,
            {
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            }
          );

          if (response.data) {
            this.departmentScheduleUsers = [
              ...this.departmentScheduleUsers,
              response.data,
            ];
            this.newUserEmail = "";
            // Reset the text field validation state
            this.$refs.newUser.reset();
          }
        } catch (error) {
          window.console.error("Error adding new user email", error);
        }
      }
    },
    getDepartmentTitleByCode(code) {
      const department = this.departments.find((d) => d.code === code);
      return department ? `${department.title} (${department.code})` : code;
    },
    async handleDeleteDepartmentCode(userId, codeId) {
      if (this.checkSessionExpire()) {
        return;
      }
      const accessToken = sessionStorage.getItem("accessToken");
      try {
        const response = await axios.delete(
          `${this.apiUrl}/v1/department_schedule_user/${userId}/department_codes/${codeId}/`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );

        if (response.status === 204) {
          const newUsers = this.departmentScheduleUsers.map((user) => {
            if (user.id === userId) {
              return {
                ...user,
                department_codes: user.department_codes.filter(
                  (code) => code.id !== codeId
                ),
              };
            }
            return user;
          });
          this.departmentScheduleUsers = newUsers;
        }
      } catch (error) {
        window.console.error("Error deleting the departmentcode:", error);
      }
    },
    async handleDeleteUser() {
      this.deleteUserDialog = false;
      if (this.checkSessionExpire()) {
        return;
      }
      const accessToken = sessionStorage.getItem("accessToken");
      try {
        const response = await axios.delete(
          `${this.apiUrl}/v1/department_schedule_user/${this.selectedUserId}/`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );
        if (response.status === 204) {
          const newUsers = this.departmentScheduleUsers.filter(
            (user) => user.id !== this.selectedUserId
          );
          this.departmentScheduleUsers = newUsers;
        }
      } catch (error) {
        window.console.error("Error deleting the user:", error);
        // Handle the error, e.g., show a notification to the user
      }
    },
    async handleAddNewDepartmentCode(userId) {
      if (!this.newDepartmentCode[userId] || this.checkSessionExpire()) {
        return;
      }
      const accessToken = sessionStorage.getItem("accessToken");
      const newPost = {
        department_code: this.newDepartmentCode[userId],
      };
      try {
        const response = await axios.post(
          `${this.apiUrl}/v1/department_schedule_user/${userId}/department_codes/`,
          newPost,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );

        if (response.data) {
          const newUsers = this.departmentScheduleUsers.map((users) => {
            if (users.id === userId) {
              return {
                ...users,
                department_codes: [...users.department_codes, response.data],
              };
            }
            return users;
          });
          this.departmentScheduleUsers = newUsers;
          this.newDepartmentCode[userId] = "";
        }
      } catch (error) {
        window.console.error("Error posting the department code:", error);
        // Handle the error, e.g., show a notification to the user
      }
    },
    handleSessionExpire() {
      this.sessionExpireDialog = false;
      this.$router.push("/office_hour_manager");
    },
    checkSessionExpire() {
      const expiryTime = sessionStorage.getItem("expiryTime");
      const currentTime = new Date().getTime();
      if (currentTime >= expiryTime) {
        this.sessionExpireDialog = true;
        return true;
      }
      return false;
    },
  },
  watch: {
    search() {
      this.page = 1;
    },
  },
};
</script>

<style scoped>
h1 {
  color: #000 !important;
  font-family: Graphik !important;
  font-size: 53px !important;
  font-style: normal !important;
  font-weight: 900 !important;
  line-height: normal !important;
}
h2 {
  color: #000 !important;
  font-family: Graphik !important;
  font-size: 30px !important;
  font-style: normal !important;
  font-weight: 300 !important;
  line-height: normal !important;
}
.description {
  color: #85817c !important;
  font-family: Graphik !important;
  font-size: 16px !important;
  font-style: normal !important;
  font-weight: 400 !important;
  line-height: 34px !important;
}

.description a {
  color: black !important;
  font-weight: 900 !important;
  text-decoration: underline;
}


.custom-label {
  color: #85817c !important;
  font-family: Graphik;
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
}
.v-data-table {
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
}

.department-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex: 1;
}

.department-row p {
  margin: 0;
}

.v-btn--active {
  background-color: #1976d2;
  color: white;
}

.custom-table-header {
  background-color: #f1efed;
  box-shadow: 0px -1px 0px 0px #edf2f7 inset;
}
.custom-table-header:first-child {
  border-top-left-radius: 16px;
}
.custom-table-header:last-child {
  border-top-right-radius: 16px;
}
.custom-table-header-text {
  color: #4f2c1d;
  font-family: Graphik;
  font-size: 10px;
  font-style: normal;
  font-weight: 600;
  line-height: 16px;
  letter-spacing: 0.6px;
  text-transform: uppercase;
}
.even-row {
  background-color: #f5f5f5; /* Light gray background */
}

.edit-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #ffb500 !important;
  height: 30px !important;
}
.delete-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(119, 119, 118, 1) !important;
  height: 30px !important;
  color: white;
}
.my-custom-sheet {
  background-color: rgba(0, 0, 0, 0.03) !important;
  border-radius: 8px;
  width: 100% !important;
}
.form-title {
  padding: 16px 30px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.form-title-text {
  color: var(--black-087, rgba(0, 0, 0, 0.87));
  font-family: Graphik;
  font-size: 20px;
  font-style: normal;
  font-weight: 600;
  line-height: 32px; /* 160% */
  letter-spacing: 0.25px;
  margin: 0;
}
.add-new-user-btn {
  display: flex;
  height: 36px;
  padding: 0px 16px;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}
.user-container {
  max-width: 300px;
}
.user-container >>> .v-input__slot {
  background-color: white;
  border-radius: 8px;
}
.add-department-code-btn {
  display: flex;
  height: 36px;
  padding: 0px 16px;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}
</style>
